// SunriseWeddingVariant — w08 Sahar.
//
// Dawn-pastel intimate elopement guest site for Madina & Eldor at Chimgan.
//
// Signature element:
//   * On hero load, a sun (SVG circle) translates from the bottom of the
//     frame to the top of the hero panel over 1.6s ease-out. Reduced
//     motion shows the sun statically positioned at the final spot.
//
// Unique-to-Sahar section:
//   * "Tomorrow's sunrise" — shows the official sunrise time + 12 stages
//     of light (twilight progressions) as a quiet timeline.
//
// Sound budget (3–4 triggers, all gentle):
//   1. Hero "save the date" CTA → tap
//   2. Section reveal (first hit only) → swoosh
//   3. RSVP "yes" → chime
//   4. RSVP "no" → tap
import { useCallback, useEffect, useRef, useState } from "react";
import { useSound } from "@/lib/sound";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── Reveal-on-scroll ──────────────────────────────────────────────────
interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  reduce: boolean;
  onFirstReveal?: () => void;
}

function Reveal({ children, delay = 0, reduce, onFirstReveal }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  const firedRef = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!firedRef.current) {
              firedRef.current = true;
              onFirstReveal?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onFirstReveal]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(22px)",
        transition: reduce ? "none" : `opacity 1s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Tomorrow's sunrise — unique Sahar section ─────────────────────────
interface SunriseSectionProps {
  accent: string;
  ink: string;
  bg: string;
  font: string;
  reduce: boolean;
}

// 12 stages of dawn light, each anchored to a real-feeling minute offset
// from sunrise (negative = before sunrise, positive = after). The actual
// sunrise time for Chimgan on 18 May 2026 is approximately 05:30 local.
const SUNRISE_STAGES: Array<{ time: string; label: string }> = [
  { time: "04:30", label: "Astronomik tong" },
  { time: "04:50", label: "Dengiz tongi" },
  { time: "05:05", label: "Fuqarolik tongi" },
  { time: "05:20", label: "Birinchi nur" },
  { time: "05:25", label: "Tog' tepasida shafaq" },
  { time: "05:30", label: "Quyosh chiqishi" },
  { time: "05:35", label: "Oltin nur" },
  { time: "05:45", label: "Issiq nur" },
  { time: "06:00", label: "Tong yorqinligi" },
  { time: "06:30", label: "Tiniq havo" },
  { time: "07:00", label: "Tong nonushtasi" },
  { time: "08:00", label: "Tog' yo'liga chiqish" },
];

function SunriseSection({ accent, ink, bg, font, reduce: _reduce }: SunriseSectionProps) {
  return (
    <section
      style={{
        padding: "100px 32px",
        borderTop: `1px solid ${ink}22`,
        textAlign: "center",
        background: `linear-gradient(180deg, ${bg} 0%, ${accent}0e 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.4em",
          color: accent,
          marginBottom: 16,
        }}
      >
        ERTANGI SAHAR · 18.05.2026
      </div>
      <h2
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: "clamp(36px, 5vw, 56px)",
          fontWeight: 400,
          marginBottom: 8,
          color: ink,
        }}
      >
        Quyosh chiqadi 05:30 da
      </h2>
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 16,
          fontStyle: "italic",
          color: `${ink}99`,
          marginBottom: 56,
          maxWidth: 520,
          margin: "0 auto 56px",
        }}
      >
        Chimgan tog'larida tongning o'n ikki bosqichi — har bir bosqich
        boshqacha nur. Biz birinchi nurdan boshlab birga bo'lamiz.
      </p>

      {/* 12 stages of light — quiet vertical timeline */}
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 0,
          textAlign: "left",
        }}
      >
        {SUNRISE_STAGES.map((stage, i) => {
          // Color shifts from cool dawn to warm sunlight as i increases.
          const t = i / (SUNRISE_STAGES.length - 1);
          const dotOpacity = 0.25 + t * 0.7;
          const isPeak = i === 5; // sunrise itself
          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "70px 16px 1fr",
                alignItems: "center",
                gap: 16,
                padding: "10px 0",
                borderBottom: i < SUNRISE_STAGES.length - 1 ? `1px solid ${ink}11` : "none",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: isPeak ? accent : `${ink}88`,
                }}
              >
                {stage.time}
              </div>
              <div
                aria-hidden
                style={{
                  width: isPeak ? 12 : 8,
                  height: isPeak ? 12 : 8,
                  borderRadius: "50%",
                  background: isPeak
                    ? `radial-gradient(circle, #FFE6C4 0%, ${accent} 70%)`
                    : accent,
                  opacity: isPeak ? 1 : dotOpacity,
                  boxShadow: isPeak ? `0 0 12px ${accent}66` : "none",
                  margin: "0 auto",
                }}
              />
              <div
                style={{
                  fontFamily: font,
                  fontStyle: "italic",
                  fontSize: isPeak ? 20 : 16,
                  fontWeight: 400,
                  color: isPeak ? ink : `${ink}cc`,
                }}
              >
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Hero rising-sun signature ─────────────────────────────────────────
interface HeroSunProps {
  accent: string;
  reduce: boolean;
}

function HeroSun({ accent, reduce }: HeroSunProps) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        style={{ position: "absolute", inset: 0 }}
      >
        <defs>
          <radialGradient id="sahar-hero-sun" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FFF1D6" stopOpacity="1" />
            <stop offset="55%" stopColor={accent}  stopOpacity="0.65" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Sun rises from below frame to roughly 30% from the top */}
        <g
          style={{
            transform: reduce ? "translateY(180px)" : "translateY(640px)",
            animation: reduce ? "none" : "saharSunRise 1.6s ease-out 0.2s forwards",
            transformOrigin: "center",
          }}
        >
          <circle cx="400" cy="300" r="180" fill="url(#sahar-hero-sun)" />
        </g>
      </svg>
      <style>{`
        @keyframes saharSunRise {
          from { transform: translateY(640px); }
          to   { transform: translateY(180px); }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
export function SunriseWeddingVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();
  const photos = t.photos || [];
  const heroSrc = photos[0];

  // Sound-wrapped RSVP setter.
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      else if (v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  // Section reveal swoosh — only fire once.
  const firstSwooshRef = useRef(false);
  const onFirstReveal = useCallback(() => {
    if (firstSwooshRef.current) return;
    firstSwooshRef.current = true;
    play("swoosh");
  }, [play]);

  const [n1, n2] = t.names.split("&").map((s) => s.trim());

  return (
    <div className="sahar-variant" style={{ background: t.bg, color: t.ink }}>
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section
        className="sahar-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          color: "#FFFFFF",
        }}
      >
        {/* Full-bleed sunrise photo */}
        <GuestPhoto
          src={heroSrc}
          fallbackBg={t.bg}
          fallbackTone={t.accent}
          style={{ position: "absolute", inset: 0 }}
        />

        {/* Dawn tint over the photo so the light reads warm regardless */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `
              linear-gradient(180deg, rgba(50,40,40,0.10) 0%, rgba(50,30,30,0) 35%, rgba(40,25,20,0.55) 100%),
              radial-gradient(120% 80% at 50% 70%, ${t.accent}33 0%, ${t.accent}00 60%)
            `,
          }}
        />

        {/* Signature: rising sun */}
        <HeroSun accent={t.accent} reduce={reduce} />

        {/* Top eyebrow */}
        <div
          className="sahar-eyebrow"
          style={{
            position: "absolute",
            top: 32,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 14,
            color: "rgba(255,255,255,0.94)",
            zIndex: 2,
          }}
        >
          <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.6)" }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.45em",
            }}
          >
            SAHAR · CHIMGAN · 2026
          </span>
          <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.6)" }} />
        </div>

        {/* Centered name plate */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 24px",
          }}
        >
          <div
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(20px, 2.4vw, 26px)",
              fontWeight: 400,
              color: "rgba(255,255,255,0.92)",
              marginBottom: 18,
            }}
          >
            tongning birinchi nuri ostida
          </div>

          <h1
            className="sahar-couple"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(56px, 9vw, 112px)",
              fontWeight: 400,
              lineHeight: 0.95,
              letterSpacing: "0.01em",
              margin: 0,
              color: "#FFFFFF",
              textShadow: "0 1px 24px rgba(20,20,30,0.45)",
            }}
          >
            {n1}
            <span
              style={{
                display: "block",
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "0.36em",
                fontWeight: 300,
                color: t.accent,
                margin: "0.15em 0 0.1em",
              }}
            >
              va
            </span>
            {n2}
          </h1>

          <div
            style={{
              marginTop: 28,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.36em",
              color: "rgba(255,255,255,0.95)",
            }}
          >
            {t.date} · {t.venue.toUpperCase()}
          </div>

          <button
            type="button"
            onClick={() => {
              play("tap");
              const el = document.getElementById("sahar-rsvp");
              if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
              }
            }}
            style={{
              marginTop: 36,
              padding: "14px 28px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.85)",
              color: "rgba(255,255,255,0.95)",
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.32em",
              cursor: "pointer",
              backdropFilter: "blur(2px)",
            }}
          >
            SAHARGA QO'SHILING
          </button>
        </div>

        {/* Scroll cue */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 18,
            color: "rgba(255,255,255,0.7)",
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.4em",
          }}
        >
          ↓ PASTGA
        </div>
      </section>

      {/* ─── COUNTDOWN ──────────────────────────────────────── */}
      <Reveal reduce={reduce} onFirstReveal={onFirstReveal}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ─── STORY ──────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="bizning hikoyamiz"
          title="Chimgan tongida"
          body="Bir necha yil oldin Chimgan tog'larida tongni birga kutib oldik. Quyosh chiqqanida bir-birimizga qaradik — va o'sha kuni hayotimizning yo'nalishi belgilandi. Endi xuddi o'sha tongni — tog'lar, jimlik va birinchi nur ostida — siz bilan birga nishonlamoqchimiz."
        />
      </Reveal>

      {/* ─── TOMORROW'S SUNRISE (unique to Sahar) ─────────── */}
      <Reveal reduce={reduce}>
        <SunriseSection accent={t.accent} ink={t.ink} bg={t.bg} font={t.font} reduce={reduce} />
      </Reveal>

      {/* ─── GALLERY ────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ─── SCHEDULE ───────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["05:30", "Quyosh chiqishi"],
            ["06:00", "Nikoh va'dasi"],
            ["07:00", "Tong nonushtasi"],
            ["09:00", "Tog'da fotosessiya"],
            ["11:00", "Birgalikda sayrga"],
          ]}
        />
      </Reveal>

      {/* ─── MAP ────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="Chimgan tog'lari, Beldersoy yo'li, Toshkent viloyati"
        />
      </Reveal>

      {/* ─── RSVP ───────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div id="sahar-rsvp">
          <RsvpSection
            t={t}
            isDark={isDark}
            rsvp={ctx.rsvp.rsvp}
            setRsvp={setRsvpWithSound}
            guests={ctx.rsvp.guests}
            setGuests={ctx.rsvp.setGuests}
            guestName={ctx.rsvp.guestName}
            setGuestName={ctx.rsvp.setGuestName}
            slug={ctx.slug}
          />
        </div>
      </Reveal>

      {/* ─── SHARE ──────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      {/* ─── FOOTER ─────────────────────────────────────────── */}
      <GuestFooter t={t} />

      <style>{`
        @media (max-width: 768px) {
          .sahar-variant .sahar-hero {
            min-height: min(92vh, 700px);
            padding: 64px 18px 48px !important;
          }
          .sahar-variant .sahar-couple {
            font-size: clamp(36px, 12vw, 72px) !important;
          }
          .sahar-variant button { min-height: 44px; }
          .sahar-variant section { padding-left: 18px; padding-right: 18px; }
          .sahar-eyebrow { top: max(16px, env(safe-area-inset-top, 16px)) !important; }
        }
      `}</style>
    </div>
  );
}
